/**
 * Per-language I/O flags and the copy they generate.
 * sttConfigured is runtime (key present). Flags are product intent.
 * hi-en is a TTS route, not an 19th language — excluded from the "18" count.
 */

export const LANGUAGE_CAPABILITIES = Object.freeze([
  { code: 'en', stt: true, text: true, tts: true },
  { code: 'hi', stt: true, text: true, tts: true },
  { code: 'hi-en', stt: true, text: true, tts: true },
  { code: 'mr', stt: true, text: true, tts: true },
  { code: 'ta', stt: true, text: true, tts: false },
  { code: 'te', stt: true, text: true, tts: false },
  { code: 'kn', stt: true, text: true, tts: false },
  { code: 'bn', stt: true, text: true, tts: false },
  { code: 'gu', stt: true, text: true, tts: false },
  { code: 'es', stt: true, text: true, tts: false },
  { code: 'fr', stt: true, text: true, tts: false },
  { code: 'de', stt: true, text: true, tts: false },
  { code: 'it', stt: true, text: true, tts: false },
  { code: 'nl', stt: true, text: true, tts: false },
  { code: 'pt', stt: true, text: true, tts: false },
  { code: 'ru', stt: true, text: true, tts: false },
  { code: 'ja', stt: true, text: true, tts: false },
  { code: 'ko', stt: true, text: true, tts: false },
  { code: 'zh', stt: true, text: true, tts: false }
]);

const TTS_LABELS = Object.freeze({
  en: 'English',
  es: 'Spanish',
  hi: 'Hindi',
  'hi-en': 'Hinglish',
  mr: 'Marathi'
});

/** Native chips for the 18 writing languages. hi-en is a TTS route, not a 19th chip. */
export const TEXT_LANGUAGE_CHIPS = Object.freeze([
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' }
]);

/** Languages with a proven STT + LLM + TTS path for US launch. */
export const SHIPPED_LANGUAGE_CAPABILITIES = Object.freeze([
  { code: 'en', stt: true, text: true, tts: true },
  { code: 'es', stt: true, text: true, tts: true }
]);

export function countTextLanguages(languages = LANGUAGE_CAPABILITIES) {
  return languages.filter(l => l.text && l.code !== 'hi-en').length;
}

export function ttsLanguageNames(languages = LANGUAGE_CAPABILITIES) {
  return languages.filter(l => l.tts).map(l => TTS_LABELS[l.code] || l.code);
}

function formatList(names) {
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function voiceCopy(languages = LANGUAGE_CAPABILITIES, { sttConfigured = false } = {}) {
  const n = countTextLanguages(languages);
  if (!sttConfigured) {
    return `Ask in writing in ${n} languages.`;
  }
  const spoken = formatList(ttsLanguageNames(languages));
  return `Speak in any of ${n} languages. Vani talks back in ${spoken} today; the rest reply in text.`;
}

export function voiceHeadline(languages = LANGUAGE_CAPABILITIES, { sttConfigured = false } = {}) {
  const n = countTextLanguages(languages);
  return sttConfigured
    ? `Speak in any of ${n} languages.`
    : `Ask in writing in ${n} languages.`;
}

export function voiceStatCaption(languages = LANGUAGE_CAPABILITIES, { sttConfigured = false } = {}) {
  void languages;
  return sttConfigured ? 'languages you can speak' : 'languages you can write in';
}

/** Indexed / OG default: true without a live STT key. */
export const VOICE_COPY = voiceCopy(LANGUAGE_CAPABILITIES, { sttConfigured: false });
export const VOICE_HEADLINE = voiceHeadline(LANGUAGE_CAPABILITIES, { sttConfigured: false });
